import React, { useState, useEffect } from 'react';
import { X, Spinner } from '@phosphor-icons/react';
import { domainsApi } from '../services/api';

const DomainModal = ({ domain, registrars, dnsProviders, ambientes, empresas, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    dominio: '',
    ambiente_input: '',
    empresa_input: '',
    registrador: '',
    dns: '',
    vencimiento: null,
    notas: '',
    activo: true,
  });
  const [loading, setLoading] = useState(false);
  const [verifyingWhois, setVerifyingWhois] = useState(false);
  const [error, setError] = useState('');
  const [whoisMessage, setWhoisMessage] = useState('');

  useEffect(() => {
    if (domain) {
      setFormData({
        dominio: domain.dominio,
        ambiente_input: domain.ambiente_name || '',
        empresa_input: domain.empresa_name || '',
        registrador: domain.registrador,
        dns: domain.dns,
        vencimiento: domain.vencimiento || null,
        notas: domain.notas || '',
        activo: domain.activo,
      });
    }
  }, [domain]);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setWhoisMessage('');
    setLoading(true);

    try {
      let createdDomain;
      if (domain) {
        await domainsApi.update(domain.id, formData);
      } else {
        const response = await domainsApi.create(formData);
        createdDomain = response.data;
        
        // Verificar WHOIS automáticamente para nuevo dominio
        if (createdDomain && createdDomain.id) {
          setWhoisMessage('Verificando fecha de vencimiento con WHOIS...');
          setVerifyingWhois(true);
          try {
            await domainsApi.checkExpiration(createdDomain.id);
            setWhoisMessage('✓ Fecha de vencimiento verificada con WHOIS');
          } catch (whoisError) {
            setWhoisMessage('⚠ No se pudo verificar con WHOIS. Puedes verificarlo manualmente desde el listado.');
          }
          setVerifyingWhois(false);
          await new Promise(resolve => setTimeout(resolve, 1500));
        }
      }
      await onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar el dominio');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-zinc-200 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {domain ? 'Editar Dominio' : 'Nuevo Dominio'}
          </h2>
          <button
            data-testid="modal-close-button"
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-900"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="label">Dominio *</label>
              <input
                data-testid="domain-name-input"
                type="text"
                name="dominio"
                className="input"
                value={formData.dominio}
                onChange={handleChange}
                required
                placeholder="ejemplo.com"
              />
            </div>

            <div>
              <label className="label">Ambiente *</label>
              <input
                data-testid="domain-ambiente-input"
                type="text"
                name="ambiente_input"
                className="input"
                value={formData.ambiente_input}
                onChange={handleChange}
                required
                placeholder="Producción, Desarrollo, etc."
                list="ambientes-list"
              />
              <datalist id="ambientes-list">
                {ambientes.map((amb) => (
                  <option key={amb.id} value={amb.name} />
                ))}
              </datalist>
            </div>

            <div>
              <label className="label">Empresa *</label>
              <input
                data-testid="domain-empresa-input"
                type="text"
                name="empresa_input"
                className="input"
                value={formData.empresa_input}
                onChange={handleChange}
                required
                placeholder="Nombre de la empresa"
                list="empresas-list"
              />
              <datalist id="empresas-list">
                {empresas.map((emp) => (
                  <option key={emp.id} value={emp.name} />
                ))}
              </datalist>
            </div>

            <div>
              <label className="label">Registrador *</label>
              <select
                data-testid="domain-registrar-select"
                name="registrador"
                className="input"
                value={formData.registrador}
                onChange={handleChange}
                required
              >
                <option value="">Seleccionar...</option>
                {registrars.map((reg) => (
                  <option key={reg.id} value={reg.id}>
                    {reg.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">Proveedor DNS *</label>
              <select
                data-testid="domain-dns-select"
                name="dns"
                className="input"
                value={formData.dns}
                onChange={handleChange}
                required
              >
                <option value="">Seleccionar...</option>
                {dnsProviders.map((dns) => (
                  <option key={dns.id} value={dns.id}>
                    {dns.name}
                  </option>
                ))}
              </select>
            </div>

            {domain && (
              <div className="md:col-span-2">
                <label className="label">Fecha de Vencimiento</label>
                <input
                  data-testid="domain-expiration-input"
                  type="date"
                  name="vencimiento"
                  className="input"
                  value={formData.vencimiento}
                  onChange={(e) => setFormData({
                    ...formData,
                    vencimiento: e.target.value || null
                  })}
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Puedes editar la fecha manualmente o usar el botón "Verificar" en el listado.
                </p>
              </div>
            )}

            {!domain && (
              <div className="md:col-span-2 p-4 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-800">
                  ℹ️ <strong>Nota:</strong> La fecha de vencimiento se verificará automáticamente usando WHOIS después de crear el dominio.
                </p>
              </div>
            )}

            <div className="md:col-span-2">
              <label className="label">Notas</label>
              <textarea
                data-testid="domain-notes-input"
                name="notas"
                className="input"
                rows="3"
                value={formData.notas}
                onChange={handleChange}
                placeholder="Notas adicionales sobre el dominio"
              />
            </div>

            <div className="md:col-span-2 flex items-center">
              <input
                data-testid="domain-active-checkbox"
                type="checkbox"
                name="activo"
                id="activo"
                className="mr-2 h-4 w-4 text-[#002FA7] focus:ring-[#002FA7] border-zinc-300 rounded"
                checked={formData.activo}
                onChange={handleChange}
              />
              <label htmlFor="activo" className="text-sm font-semibold text-zinc-700">
                Dominio activo
              </label>
            </div>

            {whoisMessage && (
              <div className="md:col-span-2 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
                {whoisMessage}
              </div>
            )}
          </div>

          <div className="mt-6 flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 border border-zinc-300 rounded font-semibold text-zinc-700 hover:bg-zinc-50"
              disabled={loading || verifyingWhois}
            >
              Cancelar
            </button>
            <button
              data-testid="modal-save-button"
              type="submit"
              disabled={loading || verifyingWhois}
              className="btn-primary flex items-center gap-2"
            >
              {(loading || verifyingWhois) && <Spinner className="animate-spin" size={16} />}
              {domain ? 'Actualizar' : 'Crear'} Dominio
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DomainModal;
